<template>
  <div class="max-w-6xl mx-auto px-4">
    <div
      class="grid gap-5 mb-5 grid-rows-1"
      :class="[
        sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION
          ? 'grid-cols-2'
          : 'grid-cols-1',
      ]"
    >
      <PreGroupcallView
        v-if="sessionStore.currentPhase === VICE_SESSION_PHASES.INTRODUCTION"
        class="camera-preview"
      >
        <template v-slot:camera-preview>
          <StreamPlayerLocal
            name="Clinician locl preview SPLN"
            class="camera"
            :isMinimizable="false"
          />
        </template>
      </PreGroupcallView>

      <template v-if="sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION">
        <!-- Internal camera  -->
        <template
          v-for="(cameraType, cameraName) in multiCameraDevicesStore.allCameraTypes"
          :key="cameraType.uniqueId"
        >
          <div
            class="relative w-full h-0"
            style="padding-top: 55.59%"
            v-if="cameraType.isActive && cameraName == CAMERA_TYPES.INTERNAL_WEBCAM"
          >
            <div class="absolute h-full w-full top-0 left-0">
              <StreamPlayerLocal
                :showTitle="true"
                :isMinimizable="false"
                :showControls="true"
                class="rounded camera"
              />
            </div>
          </div>
        </template>

        <!-- Add smartglasses button -->
        <AddDeviceButton
          data-cy="open-smartglasses-modal"
          icon="Glasses"
          label="Smart&nbsp;Glasses"
          :isBigTile="true"
          @click="multiCameraDevicesStore.openPreviewModal('smartGlass')"
          v-if="!multiCameraDevicesStore.allExternalDevicePreview[DISPLAY_NAME.VUZIX].isActive"
        />

        <!-- Smartglasses stream -->
        <template
          v-for="externalPreviewDevice in multiCameraDevicesStore.allExternalDevicePreview"
          :key="externalPreviewDevice.jitsiId"
        >
          <div v-if="externalPreviewDevice.isActive" class="relative">
            <LocalVideoDisplay
              :showTitle="true"
              :showControls="true"
              :isExternalPreview="true"
              :cameraType="externalPreviewDevice"
              :setVideoRef="multiCameraDevicesStore.setPreviewVideoRef"
              :setAudioRef="multiCameraDevicesStore.setPreviewAudioRef"
            />
          </div>
        </template>
      </template>
    </div>

    <div
      class="grid grid-rows-1 gap-5"
      :class="[
        sessionStore.currentPhase === VICE_SESSION_PHASES.INTRODUCTION
          ? 'grid-cols-6'
          : 'grid-cols-5',
      ]"
    >
      <!-- External camera preview. -->
      <template
        v-for="(cameraType, cameraName) in multiCameraDevicesStore.allCameraTypes"
        :key="cameraType.uniqueId"
      >
        <div
          class="relative w-full h-0"
          style="padding-top: 55.59%"
          v-if="cameraType.isActive && cameraName.includes('External Camera')"
        >
          <div class="absolute h-full w-full top-0 left-0">
            <LocalVideoDisplay
              :showTitle="true"
              :showControls="true"
              :showAvatar="false"
              :cameraType="cameraType"
              :toggleCamera="multiCameraDevicesStore.toggleCamera"
              :toggleMicrophone="multiCameraDevicesStore.toggleMicrophone"
              :setVideoRef="multiCameraDevicesStore.setVideoRef"
              :setAudioRef="multiCameraDevicesStore.setAudioRef"
            />
          </div>
        </div>
      </template>
      <!-- Add external camera button -->
      <AddDeviceButton
        data-cy="open-external-camera-modal"
        @click="addExternalCamera('External Camera')"
        icon="Camera"
        label="Camera"
        v-if="shouldAddAdditionalCamera"
      />

      <template
        v-for="(cameraType, cameraName) in multiCameraDevicesStore.allCameraTypes"
        :key="cameraType.uniqueId"
      >
        <div
          class="relative w-full h-0"
          style="padding-top: 55.59%"
          v-if="cameraType.isActive && cameraName.includes('External Monitor')"
        >
          <div class="absolute h-full w-full top-0 left-0">
            <LocalVideoDisplay
              :showTitle="true"
              :showControls="true"
              :cameraType="cameraType"
              :toggleCamera="multiCameraDevicesStore.toggleCamera"
              :toggleMicrophone="multiCameraDevicesStore.toggleMicrophone"
              :setVideoRef="multiCameraDevicesStore.setVideoRef"
              :setAudioRef="multiCameraDevicesStore.setAudioRef"
            />
          </div>
        </div>
      </template>
      <!-- Add external AV device button -->
      <template v-if="externalDevicesAvailable">
        <PreConsultationExternalAudioDevicePreview
          :device="EXTERNAL_CONNECTED_AUDIO_DEVICES.STETHOSCOPE"
        />
        <PreConsultationExternalVideoDevicePreview
          :device="EXTERNAL_CONNECTED_VIDEO_DEVICES.OTOSCOPE"
        />
        <PreConsultationExternalVideoDevicePreview
          :device="EXTERNAL_CONNECTED_VIDEO_DEVICES.DERMATOSCOPE"
        />
      </template>

      <!-- Add external monitor button -->
      <PreConsultationExternalVideoDevicePreview v-else device="Medical Device" />

      <!-- Add external monitor button -->
      <PreConsultationExternalVideoDevicePreview
        :device="EXTERNAL_CONNECTED_VIDEO_DEVICES.MONITOR"
      />

      <template v-if="sessionStore.currentPhase === VICE_SESSION_PHASES.INTRODUCTION">
        <!-- Add smartglasses button -->
        <AddDeviceButton
          data-cy="open-smartglasses-modal"
          icon="Glasses"
          label="Smart&nbsp;Glasses"
          @click="multiCameraDevicesStore.openPreviewModal('smartGlass')"
          v-if="!multiCameraDevicesStore.allExternalDevicePreview[DISPLAY_NAME.VUZIX].isActive"
        />

        <!-- Smartglasses stream -->
        <template
          v-for="externalPreviewDevice in multiCameraDevicesStore.allExternalDevicePreview"
          :key="externalPreviewDevice.jitsiId"
        >
          <div v-if="externalPreviewDevice.isActive" class="relative">
            <LocalVideoDisplay
              :showTitle="true"
              :showControls="true"
              :isExternalPreview="true"
              :cameraType="externalPreviewDevice"
              :setVideoRef="multiCameraDevicesStore.setPreviewVideoRef"
              :setAudioRef="multiCameraDevicesStore.setPreviewAudioRef"
            />
          </div>
        </template>
      </template>
    </div>
    <ConnectExternalDeviceModal
      data-cy="open-external-device-modal"
      @close="shouldCloseModal"
      :open="multiCameraDevicesStore.showPreviewModal"
      v-if="multiCameraDevicesStore.showPreviewModal"
    ></ConnectExternalDeviceModal>
  </div>
</template>

<script>
import { computed, watch, onMounted } from 'vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import {
  CAMERA_TYPES,
  DISPLAY_NAME,
  EXTERNAL_CONNECTED_VIDEO_DEVICES,
  EXTERNAL_CONNECTED_AUDIO_DEVICES,
  CONNECT_EXTERNAL_DEVICE_MODAL_LABELS,
  VICE_SESSION_PHASES,
} from '@/Utils/vice-constants';
import AddDeviceButton from '../Buttons/AddDeviceButton.vue';
import LocalVideoDisplay from '@/Components/LocalVideoDisplay.vue';
import ConnectExternalDeviceModal from '@/Components/Modals/ConnectExternalDeviceModal.vue';
import StreamPlayerLocal from '@/Components/Session/Grid/StreamPlayerLocal.vue';
import PreConsultationExternalVideoDevicePreview from './PreConsultationExternalVideoDevicePreview.vue';
import PreConsultationExternalAudioDevicePreview from './PreConsultationExternalAudioDevicePreview.vue';
import { generateCameraData } from '@/Utils/helpers';
import useExternalCameraComposable from '@/Composables/useExternalCameraComposable';
import useSessionStore from '@/Stores/useSessionStore';
import PreGroupcallView from '@/Components/Clinician/PreGroupcallView.vue';

export default {
  components: {
    PreGroupcallView,
    AddDeviceButton,
    LocalVideoDisplay,
    StreamPlayerLocal,
    ConnectExternalDeviceModal,
    PreConsultationExternalVideoDevicePreview,
    PreConsultationExternalAudioDevicePreview,
  },
  setup() {
    const sessionStore = useSessionStore();
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();

    // Switch to fixed number of external camera devices and hardcoded device names.
    // Due to edge case when dynamically assigning names as Additional monitor is simply an
    // external camera device so that the overall logic is not broken
    const shouldAddAdditionalCamera = computed(
      () => !multiCameraDevicesStore.allCameraTypes['External Camera']?.isActive,
    );

    const shouldAddAdditionalMonitor = computed(
      () => !multiCameraDevicesStore.allCameraTypes['External Monitor']?.isActive,
    );

    const externalCamera = multiCameraDevicesStore.connectedCameras.find(
      (camera) => camera.name === CAMERA_TYPES.EXTERNAL_WEBCAM,
    );

    const isExternalCameraConnected = computed(
      () =>
        !!multiCameraDevicesStore.groupedDeviceAsAudioAndVideo.VIDEO.Camera.find(
          (device) => device.label === externalCamera?.label,
        ),
    );

    // ExternalDevicesAvailable (Excluding Monitors)
    const externalDevicesAvailable = computed(() =>
      Boolean(
        multiCameraDevicesStore.allAvailableExternalVideoDevices.length >=
          multiCameraDevicesStore.allAvailableExternalMonitorDevices.length,
      ),
    );

    const addExternalCamera = (name, isAddedOnInit = false, cameraId = 'default') => {
      multiCameraDevicesStore.markSessionSetupAsChanged();
      multiCameraDevicesStore.setCurrentActiveCameraType(name);

      const camData = generateCameraData(
        name,
        multiCameraDevicesStore.allCameraTypes[name]?.cameraDeviceId || cameraId,
        isAddedOnInit,
      );
      multiCameraDevicesStore.addCameraType(name, {
        [name]: camData,
      });
      if (!isAddedOnInit) multiCameraDevicesStore.openPreviewModal('Camera');
    };

    const addExternalMonitor = (name, isAddedOnInit = false, cameraId = 'default') => {
      multiCameraDevicesStore.setCurrentActiveCameraType(name);

      const camData = generateCameraData(
        name,
        multiCameraDevicesStore.allCameraTypes[name]?.cameraDeviceId || cameraId,
        isAddedOnInit,
      );
      multiCameraDevicesStore.addCameraType(name, {
        [name]: camData,
      });

      if (!isAddedOnInit) multiCameraDevicesStore.openPreviewModal('Monitor');
    };

    onMounted(async () => {
      // Actualise the cached session devices setup
      if (sessionStore.currentPhase === VICE_SESSION_PHASES.INTRODUCTION) {
        await multiCameraDevicesStore
          .actualiseCachedSessionDevicesSetup()
          .then(async () => useExternalCameraComposable().handleExternalCameraConnection());
      } else {
        await useExternalCameraComposable().handleExternalCameraConnection();
      }
    });

    watch(isExternalCameraConnected, async (newVal) => {
      if (newVal) {
        await useExternalCameraComposable().handleExternalCameraConnection();
      } else {
        await multiCameraDevicesStore.removeCamera(CAMERA_TYPES.EXTERNAL_WEBCAM, false);
      }
    });

    const externalCameraName = computed(() => {
      const numberOfCameras = Number(Object.keys(multiCameraDevicesStore.allCameraTypes).length);
      if (numberOfCameras === 1) return 'External Camera';
      return `${CAMERA_TYPES.EXTERNAL_WEBCAM} ${numberOfCameras}`;
    });

    const shouldCloseModal = () => {
      multiCameraDevicesStore.showPreviewModal = !!(
        multiCameraDevicesStore.multiDeviceToAdd ==
          CONNECT_EXTERNAL_DEVICE_MODAL_LABELS.smartGlass.deviceToAdd &&
        !multiCameraDevicesStore.allExternalDevicePreview[DISPLAY_NAME.VUZIX].jitsiId
      );
      return multiCameraDevicesStore.showPreviewModal;
    };

    return {
      shouldCloseModal,
      addExternalCamera,
      addExternalMonitor,
      sessionStore,
      externalCameraName,
      multiCameraDevicesStore,
      externalDevicesAvailable,
      shouldAddAdditionalCamera,
      shouldAddAdditionalMonitor,
      CAMERA_TYPES,
      DISPLAY_NAME,
      VICE_SESSION_PHASES,
      EXTERNAL_CONNECTED_VIDEO_DEVICES,
      EXTERNAL_CONNECTED_AUDIO_DEVICES,
    };
  },
};
</script>
